import {
  getStepRailTile,
  type StepRailTile,
  stepRailConnectorColors,
  stepRailLabelColors,
} from '@zealot/ui/lib/stepRail';
import { cn } from '@zealot/ui/lib/utils';
import { cva } from 'class-variance-authority';
import { ArrowRightIcon, CheckIcon, TriangleAlertIcon } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { iconChipVariants } from './icon-chip';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

// gloss
export type StepTrackerStep = {
  id: string;
  label: string;
  icon?: ReactNode;
  tooltip?: ReactNode;
  badge?: ReactNode;
  meta?: ReactNode;
};

export type StepTrackerVariant = 'progress' | 'complete' | 'attention';
export type StepTrackerTone = 'warning' | 'destructive';

// gloss
export type StepTrackerProps = {
  steps: StepTrackerStep[];
  currentStepId?: string | null;
  variant?: StepTrackerVariant;
  tone?: StepTrackerTone;
  size?: 'sm' | 'default';
  showLabels?: boolean;
  tooltip?: ReactNode;
  'aria-label': string;
  className?: string;
};

// gloss
const ICON_SIZES = {
  sm: 12,
  default: 14,
} as const;

const stepTrackerTileFocusClasses = 'outline-none focus-visible:ring-2 focus-visible:ring-ring/50';

// gloss
const SEGMENT_INSETS = {
  sm: 14,
  default: 16,
} as const;

const stepTrackerBadgeSlotVariants = cva('flex items-end justify-center', {
  variants: {
    size: {
      sm: 'h-4',
      default: 'h-5',
    },
  },
  defaultVariants: { size: 'default' },
});

const stepTrackerMetaSlotVariants = cva('flex items-start justify-center text-center', {
  variants: {
    size: {
      sm: 'min-h-3.5',
      default: 'min-h-4',
    },
  },
  defaultVariants: { size: 'default' },
});

// gloss
const stepTrackerConnectorVariants = cva('absolute pointer-events-none rounded-full', {
  variants: {
    size: {
      sm: 'top-[25px] h-0.5',
      default: 'top-[30px] h-1',
    },
    kind: {
      track: stepRailConnectorColors.pending,
      fill: '',
    },
  },
  defaultVariants: { size: 'default', kind: 'track' },
});

// gloss
const stepTrackerLabelVariants = cva('max-w-full truncate px-2 py-0.5 text-center', {
  variants: {
    size: {
      sm: 'text-[11px]',
      default: 'text-xs',
    },
    active: {
      true: stepRailLabelColors.active,
      false: stepRailLabelColors.ahead,
    },
  },
  defaultVariants: { size: 'default', active: false },
});

// gloss
const stepTrackerGridStyle = (stepCount: number): CSSProperties => ({
  gridTemplateColumns: `repeat(${Math.max(stepCount, 1)}, minmax(0, 1fr))`,
});

const stepTrackerToneTextClasses: Record<StepTrackerTone, string> = {
  warning: 'text-warning',
  destructive: 'text-destructive',
};

type StepVisual = 'passed' | 'passed-success' | 'passed-neutral' | 'current' | 'complete' | 'attention' | 'ahead';

// gloss
const isActiveVisual = (visual: StepVisual): boolean =>
  visual === 'passed' || visual === 'passed-success' || visual === 'current' || visual === 'complete';

const getStepVisual = (
  index: number,
  currentIndex: number,
  isLast: boolean,
  variant: StepTrackerVariant,
): StepVisual => {
  if (variant === 'complete') return isLast ? 'complete' : 'passed-success';
  if (variant === 'attention') {
    if (currentIndex === -1) return 'ahead';
    if (index < currentIndex) return 'passed-neutral';
    return index === currentIndex ? 'attention' : 'ahead';
  }
  if (currentIndex === -1) return 'ahead';
  if (index < currentIndex) return 'passed';
  return index === currentIndex ? 'current' : 'ahead';
};

const getSummary = (steps: StepTrackerStep[], currentIndex: number, variant: StepTrackerVariant): string => {
  const total = steps.length;
  if (total === 0) return 'No steps';
  const currentLabel = steps[currentIndex]?.label ?? '';
  if (variant === 'complete') return `Completed all ${total} steps`;
  if (variant === 'attention') {
    return currentIndex === -1
      ? 'Needs attention'
      : `Stopped at step ${currentIndex + 1} of ${total}: ${currentLabel} — needs attention`;
  }
  return currentIndex === -1 ? `${total} steps` : `Step ${currentIndex + 1} of ${total}: ${currentLabel}`;
};

// gloss
const getStepTile = (visual: StepVisual, tone: StepTrackerTone): StepRailTile => {
  if (visual === 'attention') return getStepRailTile('current', tone);
  if (visual === 'current') return getStepRailTile('current');
  if (visual === 'ahead') return getStepRailTile('ahead');
  if (visual === 'passed-neutral') return getStepRailTile('done', 'default');
  if (visual === 'passed') return getStepRailTile('done');
  return getStepRailTile('done', 'success');
};

// gloss
const StepGlyph = ({ visual, size }: { visual: StepVisual; size: 'sm' | 'default' }) => {
  const iconSize = ICON_SIZES[size];
  if (visual === 'attention') return <TriangleAlertIcon aria-hidden size={iconSize} />;
  if (visual === 'current') return <ArrowRightIcon aria-hidden size={iconSize} />;
  if (visual === 'ahead' || visual === 'passed-neutral') return null;
  return <CheckIcon aria-hidden size={iconSize} />;
};

// gloss
const AttentionFlag = ({
  tone,
  size,
  summary,
  ariaLabel,
  tooltip,
  className,
}: {
  tone: StepTrackerTone;
  size: 'sm' | 'default';
  summary: string;
  ariaLabel: string;
  tooltip?: ReactNode;
  className?: string;
}) => {
  const tileClasses = iconChipVariants({ tone, fill: 'tinted', size, shape: 'square' });
  const icon = <TriangleAlertIcon aria-hidden size={ICON_SIZES[size]} />;

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-slot="step-tracker"
      className={cn('inline-flex items-center', className)}
    >
      <span className="sr-only">{summary}</span>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger
            aria-label={typeof tooltip === 'string' ? tooltip : ariaLabel}
            className={cn(tileClasses, stepTrackerTileFocusClasses)}
          >
            {icon}
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        <span className={tileClasses}>{icon}</span>
      )}
    </div>
  );
};

// gloss
const StepTracker = ({
  steps,
  currentStepId = null,
  variant = 'progress',
  tone = 'warning',
  size = 'default',
  showLabels = true,
  tooltip,
  className,
  'aria-label': ariaLabel,
}: StepTrackerProps) => {
  const currentIndex = currentStepId ? steps.findIndex((step) => step.id === currentStepId) : -1;
  const lastIndex = steps.length - 1;
  const summary = getSummary(steps, currentIndex, variant);

  if (variant === 'attention' && currentIndex === -1) {
    return (
      <AttentionFlag
        tone={tone}
        size={size}
        summary={summary}
        ariaLabel={ariaLabel}
        tooltip={tooltip}
        className={className}
      />
    );
  }

  if (steps.length === 0) {
    return (
      <div role="group" aria-label={ariaLabel} data-slot="step-tracker" className={cn('w-full', className)}>
        <span className="sr-only">{summary}</span>
      </div>
    );
  }

  const columnPercent = 100 / steps.length;
  const fillColorClass =
    variant === 'complete'
      ? 'bg-success'
      : variant === 'attention'
        ? 'bg-muted-foreground'
        : stepRailConnectorColors.filled;

  return (
    <div role="group" aria-label={ariaLabel} data-slot="step-tracker" className={cn('w-full', className)}>
      <span className="sr-only">{summary}</span>
      <div
        role="list"
        data-slot="step-tracker-rail"
        className="relative grid"
        style={stepTrackerGridStyle(steps.length)}
      >
        {}
        {steps.slice(0, -1).map((step, index) => {
          const isFilled = variant === 'complete' || index < currentIndex;
          return (
            <div
              key={`${step.id}-connector`}
              aria-hidden
              data-slot="step-tracker-connector"
              data-kind={isFilled ? 'fill' : 'track'}
              className={cn(
                stepTrackerConnectorVariants({ size, kind: isFilled ? 'fill' : 'track' }),
                isFilled && fillColorClass,
              )}
              style={{
                left: `calc(${(index + 0.5) * columnPercent}% + ${SEGMENT_INSETS[size]}px)`,
                width: `calc(${columnPercent}% - ${SEGMENT_INSETS[size] * 2}px)`,
              }}
            />
          );
        })}
        {steps.map((step, index) => {
          const visual = getStepVisual(index, currentIndex, index === lastIndex, variant);
          const isCurrentPosition = visual === 'current' || visual === 'attention';
          const isActiveLabel = isActiveVisual(visual);
          return (
            <div
              key={step.id}
              role="listitem"
              data-slot="step-tracker-step"
              data-state={visual}
              aria-current={isCurrentPosition ? 'step' : undefined}
              className="relative flex flex-col items-center"
            >
              <div className={stepTrackerBadgeSlotVariants({ size })}>{step.badge}</div>
              <Tooltip>
                <TooltipTrigger
                  aria-label={typeof step.tooltip === 'string' ? step.tooltip : step.label}
                  className={cn(
                    iconChipVariants({ ...getStepTile(visual, tone), size, shape: 'square' }),
                    stepTrackerTileFocusClasses,
                  )}
                >
                  {step.icon ?? <StepGlyph visual={visual} size={size} />}
                </TooltipTrigger>
                <TooltipContent>{step.tooltip ?? step.label}</TooltipContent>
              </Tooltip>
              {showLabels && (
                <span className={cn('mt-1', stepTrackerLabelVariants({ size, active: isActiveLabel }))}>
                  {step.label}
                </span>
              )}
              <div className={stepTrackerMetaSlotVariants({ size })}>{step.meta}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export type StepTrackerLegendProps = {
  steps: StepTrackerStep[];
  size?: 'sm' | 'default';
  className?: string;
};

// gloss
const StepTrackerLegend = ({ steps, size = 'default', className }: StepTrackerLegendProps) => (
  <div
    role="list"
    data-slot="step-tracker-legend"
    className={cn('grid', className)}
    style={stepTrackerGridStyle(steps.length)}
  >
    {steps.map((step) => (
      <div key={step.id} role="listitem" className={stepTrackerLabelVariants({ size, active: false })}>
        {step.label}
      </div>
    ))}
  </div>
);

export {
  StepTracker,
  StepTrackerLegend,
  stepTrackerBadgeSlotVariants,
  stepTrackerConnectorVariants,
  stepTrackerLabelVariants,
  stepTrackerMetaSlotVariants,
  stepTrackerToneTextClasses,
};
